import React from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { TaskPriority } from "@/models/Task";
import Category from "@/models/Category";
import Tag from "@/models/Tag";

export interface TaskFormProps {
  title: string;
  setTitle: (title: string) => void;
  description: string;
  setDescription: (desc: string) => void;
  categoryId: string | null;
  setCategoryId: (id: string | null) => void;
  priority: TaskPriority;
  setPriority: (priority: TaskPriority) => void;
  dueDate: string;
  setDueDate: (date: string) => void;
  selectedTagIds: string[];
  setSelectedTagIds: (ids: string[]) => void;
  categories: Category[];
  tags: Tag[];
  onSubmit: () => void;
  onCancel: () => void;
  submitLabel?: string;
  isSubmitting?: boolean;
}

export const TaskForm: React.FC<TaskFormProps> = ({
  title,
  setTitle,
  description,
  setDescription,
  categoryId,
  setCategoryId,
  priority,
  setPriority,
  dueDate,
  setDueDate,
  selectedTagIds,
  setSelectedTagIds,
  categories,
  tags,
  onSubmit,
  onCancel,
  submitLabel = "Save",
  isSubmitting = false,
}) => {
  const handleTagChange = (tagId: string) => {
    const prevTags = [...selectedTagIds];
    setSelectedTagIds(
      prevTags.includes(tagId)
        ? prevTags.filter((id) => id !== tagId)
        : [...prevTags, tagId],
    );
  };

  return (
    <Form>
      <Form.Group className="mb-3">
        <Form.Label>Title</Form.Label>
        <Form.Control
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Task title"
          autoFocus
          required
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Task description"
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Category</Form.Label>
        <Form.Select
          value={categoryId || ""}
          onChange={(e) =>
            setCategoryId(e.target.value === "" ? null : e.target.value)
          }
        >
          <option value="">No category</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Priority</Form.Label>
        <Form.Select
          value={priority}
          onChange={(e) => setPriority(e.target.value as TaskPriority)}
        >
          <option value={TaskPriority.LOW}>Low</option>
          <option value={TaskPriority.MEDIUM}>Medium</option>
          <option value={TaskPriority.HIGH}>High</option>
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Due Date</Form.Label>
        <Form.Control
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Tags</Form.Label>
        <div>
          {tags && tags.length > 0 ? (
            tags.map((tag) => (
              <Form.Check
                key={tag.id}
                inline
                type="checkbox"
                id={`tag-checkbox-${tag.id}`}
                label={
                  <span>
                    <span
                      style={{
                        display: "inline-block",
                        width: 14,
                        height: 14,
                        backgroundColor: tag.color || "#ccc",
                        borderRadius: "50%",
                        border: "1px solid #aaa",
                        marginRight: 6,
                        verticalAlign: "middle",
                      }}
                    />
                    {tag.name}
                  </span>
                }
                checked={selectedTagIds.includes(tag.id)}
                onChange={() => handleTagChange(tag.id)}
              />
            ))
          ) : (
            <div className="text-muted">No tags available.</div>
          )}
        </div>
      </Form.Group>
      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={title.trim() === "" || isSubmitting}
        >
          {submitLabel}
        </Button>
      </div>
    </Form>
  );
};
